import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '@api/api.service';
import { AccountE2eEncryption, AccountStorageTotals, AccountWithUsers, UserRole } from '@api/types';
import { environment } from '@env/environment';
import { RavIconCircled } from '@shared/icon/icon';
import { ModalService } from '@shared/modal/modal.service';
import { AccountStorageUsageService } from '@shared/services/account-storage-usage.service';
import { EncryptionHelperService } from '@shared/services/encryption-helper.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { PreferencesService } from '@shared/services/preferences.service';
import { ScopeService } from '@shared/services/scope.service';
import { EnvironmentType } from '@shared/types/environment';
import { AccountSettingsKeys } from '@shared/types/settings-keys';
import { StatusCodes } from '@shared/types/status-codes';
import { ZendeskConfig } from '@shared/types/zendesk';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { get } from 'lodash';
import { NgxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { states } from 'src/app/states';
import { PhoneNumberValidator } from '../../../validator/phone-number.validator';
import { AccountDetailsForm, AccountDetailUpdate } from './account.classes';

@Component({
  selector: 'rav-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;
  account: AccountWithUsers;
  successMessage: string;
  ravenEmail: string;
  states = states;
  conformedPhoneNumber = '';
  accountStorageTotals: AccountStorageTotals;
  UserRole: typeof UserRole = UserRole;
  RavIconCircled: typeof RavIconCircled = RavIconCircled;
  permanentDeletionEnabled: boolean;
  permanentDeletionOptions = [
    { value: true, name: 'Allowed' },
    { value: false, name: 'Not Allowed' }
  ];
  showHistoryEnabled: boolean;
  showHistoryOptions = [
    { value: true, name: 'Enabled' },
    { value: false, name: 'Disabled' }
  ];

  accountExpiresIn: number;
  isUpdatingPasswordResetPolicy = false;
  documentE2eEncryptionEnabled = false;
  documentE2eEncryptionOptions = [
    { value: true, name: 'Enabled' },
    { value: false, name: 'Disabled' }
  ];
  documentE2eEncryptionStatus: AccountE2eEncryption;

  AccountE2eEncryption: typeof AccountE2eEncryption = AccountE2eEncryption;
  subscriptions: Subscription[] = [];
  production: boolean;
  emailUsername: string;
  emailDomain: string;
  initializedZendesk = false;
  twoFactorAuthRequired = false;

  private encryptionBeta = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    public localStorage: LocalStorageService,
    public scope: ScopeService,
    private zenDesk: NgxZendeskWebwidgetService,
    private modal: ModalService,
    private accountStorageUsageService: AccountStorageUsageService,
    private preferences: PreferencesService,
    private snackBar: MatSnackBar,
    private encryptionHelper: EncryptionHelperService
  ) {
    this.encryptionHelper.accountEncryptionStatus.subscribe((status) => {
      this.documentE2eEncryptionStatus = status;
      this.documentE2eEncryptionEnabled = this.documentE2eEncryptionStatus === AccountE2eEncryption.Active;
    });
    this.production = environment.type === EnvironmentType.Production;
  }

  async ngOnInit() {
    this.buildFormGroup();
    this.getAccountDetail();
    await this.getAccountSettings();
    this.accountStorageTotals = await this.accountStorageUsageService.getStorageUsage();
    this.subscriptions.push(
      this.modal.close.subscribe((data) => {
        if (data && data.message) this.displayMessage(data.message, data.messageType);
        this.documentE2eEncryptionEnabled = this.documentE2eEncryptionStatus === AccountE2eEncryption.Active;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
  /**
   * Builds the form group by creating all the form controls for every required field.
   */
  buildFormGroup() {
    this.formGroup = this.formBuilder.group(
      {
        name: new FormControl('', Validators.required),
        phone_number: new FormControl('', Validators.required),
        address_1: new FormControl('', Validators.required),
        address_2: new FormControl(''),
        city: new FormControl('', Validators.required),
        state: new FormControl('', Validators.required),
        postal_code: new FormControl('', Validators.required)
      },
      {
        validators: PhoneNumberValidator.Validate
      }
    );
  }

  /**
   * Gets the account details
   */
  getAccountDetail(): void {
    this.successMessage = null;
    this.api.accounts.byId.get(this.localStorage.userWithToken.account_id).subscribe((account) => {
      this.account = account;
      this.accountExpiresIn = account.passwords_expire_in;
      this.conformedPhoneNumber = account.phone_number
        ? parsePhoneNumberFromString(account.phone_number).formatInternational()
        : account.phone_number;
      this.ravenEmail = account.incoming_email;
      const emailComponents = this.ravenEmail.split('@');
      this.emailUsername = emailComponents[0];
      this.emailDomain = `@${emailComponents[1]}`;
      this.formGroup.patchValue(new AccountDetailsForm(account));
    });
  }

  /**
   * Gets the account settings and assigns them to variables to be modified.
   */
  async getAccountSettings(): Promise<void> {
    const [permanentDeletionEnabled, hideOutboxes, twoFactorAuth, settings] = await Promise.all([
      this.preferences.getSetting('Account', AccountSettingsKeys.permanentDeletion),
      this.preferences.getSetting('Account', AccountSettingsKeys.hideOutboxes, false, true),
      this.preferences.getSetting('Account', AccountSettingsKeys.requireTwoFactor, false, true),
      this.api.accounts.byId.settings.get(this.localStorage.userWithToken.account_id).toPromise()
    ]);
    this.permanentDeletionEnabled = permanentDeletionEnabled;
    this.showHistoryEnabled = !hideOutboxes;
    this.encryptionBeta = !!settings[AccountSettingsKeys.encryptionBeta];
    this.twoFactorAuthRequired = twoFactorAuth;
  }

  /**
   * Updates the account details
   */
  updateAccount(): void {
    this.successMessage = null;
    const oldAccountDetails = new AccountDetailUpdate(new AccountDetailsForm(this.account));
    const newAccountDetails = new AccountDetailUpdate(this.formGroup.value);
    for (const field in oldAccountDetails) {
      if (oldAccountDetails[field] === newAccountDetails[field]) {
        delete newAccountDetails[field];
      }
    }
    if (Object.keys(newAccountDetails).length !== 0) {
      this.api.accounts.byId.patch(this.localStorage.userWithToken.account_id, newAccountDetails).subscribe(
        () => {
          this.successMessage = 'Your account details have been updated successfully';
        },
        (error) => {
          this.api.onFormError(error, this.formGroup);
          if (error.status === StatusCodes.FORBIDDEN && !!newAccountDetails.phone_number) {
            this.modal.open.next({
              modalId: 'authentication',
              request: this.api.accounts.byId.patch(this.localStorage.userWithToken.account_id, newAccountDetails),
              scope: error.headers.get('scope'),
              type: 'account'
            });
          }
        }
      );
    }
  }

  /**
   * Called when email is edited using rav-editor to update the incoming_email
   * locally and to update the email being shown in the sidebar
   *
   * @param event edition event
   */
  onEditEmailAddress(event) {
    const newUserWithToken = { ...this.localStorage.userWithToken };
    newUserWithToken.account.incoming_email = event.text;
    this.localStorage.userWithToken = newUserWithToken;
  }

  /**
   * Set permanent deletion setting.
   *
   * @param value permanent deletion setting.
   */
  onPermanentDeletionChange(event): void {
    this.permanentDeletionEnabled = event.target.value === 'true' || event.target.value === true;
    this.preferences.setAccountPreferenceSetting(this.permanentDeletionEnabled, AccountSettingsKeys.permanentDeletion);
  }

  /**
   * Update Password Reset Policy for the account
   *
   * @param number number of days to force reset passwords
   */
  onUpdateResetPasswordPolicy(days: number | null): void {
    const payload = { passwords_expire_in: days };
    this.isUpdatingPasswordResetPolicy = true;
    this.api.accounts.byId
      .patch(this.localStorage.userWithToken.account_id, payload)
      .pipe(finalize(() => (this.isUpdatingPasswordResetPolicy = false)))
      .subscribe(
        (res) => {
          this.accountExpiresIn = res.passwords_expire_in;
          this.displayMessage('Your Password Reset Policy has been updated successfully', 'success');
        },
        (error) => console.error
      );
  }

  /**
   * Update Two Factor Authentication requirement
   *
   * @param twoFactorAuth Enable or disable 2FA
   */
  onTwoFactorAuthChanged(twoFactorAuth: boolean): void {
    this.preferences.setAccountPreferenceSetting(twoFactorAuth, AccountSettingsKeys.requireTwoFactor);
    this.displayMessage('Two factor authentication updated successfully', 'success');
  }

  /**
   * Set show history setting.
   *
   * @param value show history setting.
   */
  onShowHistoryChange(event): void {
    this.showHistoryEnabled = event.target.value === 'true' || event.target.value === true;
    this.preferences.setAccountPreferenceSetting(!this.showHistoryEnabled, AccountSettingsKeys.hideOutboxes);
    this.preferences.historyVisibilityChangedAnnounceSource.next(this.showHistoryEnabled);
  }

  /**
   * When the help button is clicked, the ZenDesk web widget is launched
   */
  async onHelpClick() {
    if (!this.initializedZendesk) await this.initZendesk();
    this.zenDesk.zE('webWidget', 'show');
    this.zenDesk.zE('webWidget', 'open');
  }

  /**
   * Initializes the Zendesk service and identifies the user by adding name, email, and organization.
   */
  async initZendesk(): Promise<void> {
    await this.zenDesk.initZendesk(new ZendeskConfig());
    this.zenDesk.zE('webWidget', 'identify', {
      name: `${get(this.localStorage, 'userWithToken.first_name', '')}  ${get(
        this.localStorage,
        'userWithToken.last_name',
        ''
      )}`,
      email: get(this.localStorage, 'userWithToken.email', ''),
      organization: get(this.localStorage, 'userWithToken.account.name', '')
    });
    this.initializedZendesk = true;
  }

  /**
   * on document e2e encryption select change, open enable/disable modal
   */
  onDocumentE2eEncryptionChange(event: any): void {
    // User is enabling E2EE
    if (!this.documentE2eEncryptionEnabled && this.accountExpiresIn && this.accountExpiresIn > 0) {
      return this.displayMessage(
        'E2E Encryption cannot be enabled if Password Reset Policy is enabled. Please disable Password Reset Policy and try again.',
        'error'
      );
    }
    this.modal.open.next({ modalId: 'e2eEncryptionModal' });
  }

  /**
   * This method prevents the dropdown value to change when clicking on any option, binding the value with
   * documentE2eEncryptionEnabled which has the real value of enable/ disable
   */
  preventSelect(event) {
    event.target.value = this.documentE2eEncryptionEnabled;
  }

  /**
   * Enable access to encryption setting
   */
  canUserE2EEncryption() {
    return this.encryptionBeta || this.isRavenUser();
  }

  /**
   * Sees if the logged account is part of Raven, checking on the last 10 characters of the email if '@raven' exists s
   */
  private isRavenUser() {
    if (this.account && this.account.email) {
      return this.account.email.includes('@raven.com', this.account.email.length - 10);
    }
    return false;
  }

  /**
   * Display the snackbar message
   *
   * @param message Message to be displayed.
   * @param type Is a success message or failed message
   */
  private displayMessage(message: string, type: string): void {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: type === 'success' ? ['success-bg', 'bold', 'light-color'] : ['alert-bg', 'bold', 'light-color']
    });
  }
}
